// Token-AND directory search (SEP-174 / SEP-175).
//
// Every directory search in the app used to be a single `haystack.includes(q)`
// over the whole query string, which is why searching "Edward Richards" found
// nothing against a contact stored as "Edward Thomas Richards": the middle name
// breaks the substring. The fix is a token-AND match — split the query on
// whitespace and require EVERY token to appear somewhere in the row's
// searchable text, in any order and in any field.
//
// It lives in `packages/contract` for the same reason `contact-name.ts` does:
// the predicate has to be identical on both sides of the wire. The contacts
// service filters server-side (the relationship picker passes `?search=`) while
// the directory filters client-side over the full row set — two
// implementations of "does this row match?" would silently disagree about which
// rows exist. Pure: no DOM, no clock, no ontology.

/**
 * Leading / trailing characters that are neither a letter nor a digit. Trimmed
 * off each token so punctuation the user typed (or pasted) does not have to be
 * matched literally — see {@link searchTokens}.
 */
const TOKEN_EDGE_PUNCTUATION = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;

/**
 * Split a raw search box value into lower-cased, whitespace-free tokens.
 * An empty or whitespace-only query yields no tokens, which
 * {@link matchesAllTokens} treats as "matches everything".
 *
 * PUNCTUATION AT A TOKEN'S EDGES IS DROPPED, because the haystack is a raw
 * concatenation of stored fields and a token is matched against it literally.
 * Without this, two ordinary queries return nothing:
 *
 *   - `Ashworth, Julian` — the CRM's inverted form, typed against a contact
 *     stored as `Julian Ashworth`: the token `ashworth,` appears nowhere;
 *   - a pasted option label such as `SUR-1005 · Ashford · 2620-D 0111111`:
 *     the bare `·` separator is in no searchable field.
 *
 * Only the EDGES are trimmed, so `e.richards@example.com`, `2620-D` and
 * `07700-900123` keep their internal punctuation and still match a stored
 * value verbatim. A token that is nothing but punctuation trims to empty and
 * is dropped.
 *
 * Hoist this out of the row loop — it is the only part of the predicate that
 * depends solely on the query.
 */
export function searchTokens(query: string | null | undefined): readonly string[] {
    if (typeof query !== 'string') return [];
    return query
        .toLowerCase()
        .split(/\s+/)
        .map((token) => token.replace(TOKEN_EDGE_PUNCTUATION, ''))
        .filter((token) => token.length > 0);
}

/**
 * Join a row's searchable fields into one lower-cased haystack.
 *
 * Fields are joined with a space, which is safe precisely because tokens never
 * contain whitespace: no token can straddle two fields and produce a phantom
 * match.
 */
export function searchHaystack(fields: ReadonlyArray<string | null | undefined>): string {
    let haystack = '';
    for (const field of fields) {
        if (typeof field !== 'string' || field.length === 0) continue;
        haystack += haystack.length > 0 ? ` ${field}` : field;
    }
    return haystack.toLowerCase();
}

/**
 * True when every token appears somewhere in `fields`. Order-insensitive and
 * field-insensitive: "richards edward" and "edward richards" both match a row
 * whose name is "Edward Thomas Richards", and "richards hartley@example.com"
 * matches a row whose surname is in one field and whose email is in another.
 *
 * No tokens (an empty search box) matches every row.
 */
export function matchesAllTokens(
    tokens: readonly string[],
    fields: ReadonlyArray<string | null | undefined>,
): boolean {
    if (tokens.length === 0) return true;
    const haystack = searchHaystack(fields);
    return tokens.every((token) => haystack.includes(token));
}

/**
 * One-shot convenience for a single row — tokenise then match. Prefer
 * {@link searchTokens} + {@link matchesAllTokens} when filtering a list so the
 * query is only tokenised once.
 */
export function matchesSearchQuery(
    query: string | null | undefined,
    fields: ReadonlyArray<string | null | undefined>,
): boolean {
    return matchesAllTokens(searchTokens(query), fields);
}
